/**
 * SearchAI Analytics Module
 * Handles sending analytics events to the backend API
 */

// Define the global namespace early (Legacy Access Point)
window.SearchAI = window.SearchAI || {};

// ================================
// Core Logic and Configuration
// ================================

// Configuration is now defined globally or imported elsewhere, 
// but we define it here as a mutable object for the configure function.
const CONFIG = {
  baseUrl: 'https://private.api.searchaiengine.com/dashboard/api/v1/events', // Replace <domain> with your actual domain
  shopId: window.Shopify?.shop || 'store.myshopify.com', // Get from Shopify global or fallback
  timeout: 10000, // 10 seconds
  retryAttempts: 3,
  retryDelay: 1000 // 1 second
};

// API endpoints mapping
const ENDPOINTS = {
  search: '/search',
  product_click: '/product-click',
  add_to_cart: '/add-to-cart',
  buy_now: '/buy-now-click',
  purchase: '/purchase'
};

/**
 * Generate a unique idempotency key for requests
 */
function generateIdempotencyKey() {
  return 'idem_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
}

/**
 * Convert product ID to Shopify GID format if needed
 */
function formatProductId(productId) {
  if (!productId) return null;
  
  // If already in GID format, return as is
  if (typeof productId === 'string' && productId.startsWith('gid://shopify/Product/')) {
    return productId;
  }
  
  // Convert numeric ID to GID format
  return `gid://shopify/Product/${productId}`;
}

/**
 * Make HTTP request with retry logic
 */
async function makeRequest(url, data, options = {}) {
  const { retryCount = 0 } = options;
  
  try {
    // Note: AbortSignal.timeout is a modern standard and should work here
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Idempotency-Key': generateIdempotencyKey(),
        'ngrok-skip-browser-warning': '1',
        ...options.headers
      },
      body: JSON.stringify(data),
      signal: AbortSignal.timeout(CONFIG.timeout) 
    });

    if (!response.ok) {
      return false;
    }

    return true;
  } catch (error) {
    console.error(`SearchAI Analytics: Request failed (attempt ${retryCount + 1}):`, error);
    
    // Retry logic
    if (retryCount < CONFIG.retryAttempts - 1) {
      const delay = CONFIG.retryDelay * Math.pow(2, retryCount); // Exponential backoff
      console.log(`SearchAI Analytics: Retrying in ${delay}ms...`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return makeRequest(url, data, { ...options, retryCount: retryCount + 1 });
    }
    
    // Original behavior: throw the error on final failure
    throw error;
  }
}

// ================================
// Report Methods (Now Exported)
// ================================

/**
 * Report search event
 */
export async function reportSearch(eventData) {
  const payload = {
    shopId: CONFIG.shopId,
    searchId: eventData.search_id,
    sessionId: eventData.session_id,
    clientId: eventData.client_id,
    query: eventData.query,
    productIds: eventData.product_ids ? eventData.product_ids.map(formatProductId) : [],
    timestampMs: eventData.timestamp || Date.now()
  };

  const url = CONFIG.baseUrl + ENDPOINTS.search;
  return makeRequest(url, payload);
}


/**
 * Report product click event
 */
export async function reportProductClick(eventData) {
  const payload = {
    shopId: CONFIG.shopId,
    sessionId: eventData.session_id,
    clientId: eventData.client_id,
    productId: formatProductId(eventData.product_id),
    searchId: eventData.search_id,
    timestampMs: eventData.timestamp || Date.now()
  };

  const url = CONFIG.baseUrl + ENDPOINTS.product_click;
  return makeRequest(url, payload);
}

/**
 * Report add to cart event
 */
export async function reportAddToCart(eventData) {
  const payload = {
    shopId: CONFIG.shopId,
    sessionId: eventData.session_id,
    clientId: eventData.client_id,
    productId: formatProductId(eventData.product_id),
    price: eventData.price || '0',
    currency: eventData.currency || 'USD',
    timestampMs: eventData.timestamp || Date.now()
  };

  const url = CONFIG.baseUrl + ENDPOINTS.add_to_cart;
  return makeRequest(url, payload);
}

/**
 * Report buy now click event
 */
export async function reportBuyNowClick(eventData) {
  const payload = {
    shopId: CONFIG.shopId,
    sessionId: eventData.session_id,
    clientId: eventData.client_id,
    productId: formatProductId(eventData.product_id),
    price: eventData.price || '0',
    currency: eventData.currency || 'USD',
    timestampMs: eventData.timestamp || Date.now()
  };

  const url = CONFIG.baseUrl + ENDPOINTS.buy_now;
  return makeRequest(url, payload);
}

/**
 * Report purchase event
 */
export async function reportPurchase(eventData) {
  const payload = {
    shopId: CONFIG.shopId,
    sessionId: eventData.session_id,
    productIds: eventData.product_ids ? eventData.product_ids.map(formatProductId) : [],
    totalAmount: eventData.total_amount,
    currency: eventData.currency || 'USD',
    timestampMs: eventData.timestamp || Date.now()
  };

  const url = CONFIG.baseUrl + ENDPOINTS.purchase;
  return makeRequest(url, payload);
}

/**
 * Main report function - routes to appropriate handler based on event type
 */
export async function report(eventData) {
  if (!eventData || !eventData.type) {
    console.error('SearchAI Analytics: Invalid event data - missing type');
    return false;
  }

  try {
    console.log('SearchAI Analytics: Sending event:', eventData);
    
    let result;
    switch (eventData.type) {
      case 'search':
        result = await reportSearch(eventData);
        break;
      case 'product_click':
        result = await reportProductClick(eventData);
        break;
      case 'add_to_cart':
        result = await reportAddToCart(eventData);
        break;
      case 'buy_now':
        result = await reportBuyNowClick(eventData);
        break;
      case 'purchase':
        result = await reportPurchase(eventData);
        break;
      default:
        console.error('SearchAI Analytics: Unknown event type:', eventData.type);
        return false;
    }

    console.log('SearchAI Analytics: Event sent successfully:', result);
    return true;
  } catch (error) {
    console.error('SearchAI Analytics: Failed to send event:', error);
    return false;
  }
}

/**
 * Update configuration
 */
export function configure(newConfig) {
  Object.assign(CONFIG, newConfig);
}

/**
 * Get current configuration
 */
export function getConfig() {
  return { ...CONFIG };
}

// =============================================================================
// LEGACY GLOBAL EXPOSURE (Mocks the original IIFE output)
// =============================================================================

// Attach the public methods to the global window object, mocking the old IIFE return.
window.SearchAI.Analytics = {
    // Public API
    report: report,
    configure: configure,
    getConfig: getConfig,
    // Individual report methods for direct use
    reportSearch: reportSearch,
    reportProductClick: reportProductClick,
    reportAddToCart: reportAddToCart,
    reportBuyNowClick: reportBuyNowClick,
    reportPurchase: reportPurchase
};


// Initialize with Shopify shop domain if available
if (window.Shopify && window.Shopify.shop) {
  window.SearchAI.Analytics.configure({
    shopId: window.Shopify.shop
  });
}